import { sbEditable } from '@storyblok/storyblok-editable';
import classNames from 'classnames';
import type { ReactElement, RefObject } from 'react';
import { useRef } from 'react';
import { useChangeTransitionState } from 'util/react-transition-component/hooks/useChangeTransitionState';
import { useTransitionController } from 'util/react-transition-component';
import Text from '../../atoms/text/Text';
import type { QuoteProps } from './Quote.data';
import styles from './Quote.module.scss';
import { setupTransitionInTimeline, setupTransitionOutTimeline } from './Quote.transitions';
import useThemeScrollBlock from '../../../hooks/useThemeScrollBlock';

export default function Quote({
  isVisible,
  className,
  blok,
  canTransitionIn,
  ref,
}: QuoteProps): ReactElement {
  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<HTMLDivElement | null>;

  const transitionController = useTransitionController(
    () => ({
      ref: elementRef,
      refs: {
        elementRef,
      },
      setupTransitionInTimeline,
      setupTransitionOutTimeline,
    }),
    [],
  );

  useChangeTransitionState(transitionController, isVisible);
  useThemeScrollBlock(elementRef, blok.use_accent_color);

  return (
    <figure
      ref={elementRef}
      className={classNames(styles.quote, className, 'grid-gutter')}
      {...sbEditable(blok)}
    >
      <Text
        as="blockquote"
        size="quote"
        className={classNames({ [styles.withQuotationMarks]: blok.credit })}
        disableTriggerInOnScroll={false}
        canTransitionIn={canTransitionIn}
      >
        {blok.quote}
      </Text>

      {blok.credit && (
        <Text
          as="figcaption"
          size="body-standard"
          disableTriggerInOnScroll={false}
          canTransitionIn={canTransitionIn}
        >
          {blok.credit}
        </Text>
      )}
    </figure>
  );
}
